/**
 * BEX LOOP STUDIO - central configuration.
 *
 * Anything that could become stale (provider limits, pricing, model ids)
 * lives here with the official source URL and the date it was verified,
 * so the UI never hard-codes a promise it cannot keep.
 */

export const BRAND = {
  app: 'BEX LOOP',
  appFull: 'BEX LOOP STUDIO',
  subtitle: 'BEX CAST Visual Engine',
  content: 'BEX CAST',
  colors: {
    base: '#14161C',
    surface: '#202020',
    accent: '#FF7A00',
  },
  version: '1.0.0',
};

export const ASPECT_RATIOS = [
  { id: '9:16', label: '9:16 Vertical', ratio: 9 / 16, primary: true },
  { id: '1:1', label: '1:1 Square', ratio: 1 },
  { id: '16:9', label: '16:9 Landscape', ratio: 16 / 9 },
];

export const RESOLUTIONS = [
  { id: '1080x1920', label: '1080 x 1920 (Full HD vertical)', width: 1080, height: 1920, tier: 'high' },
  { id: '720x1280', label: '720 x 1280 (HD vertical, faster)', width: 720, height: 1280, tier: 'standard' },
];

export const LOOP_MODES = [
  { id: 'normal', label: 'Normal', hint: 'Straight repeat. Cheapest, sharpest seam.' },
  { id: 'crossfade', label: 'Crossfade', hint: 'Overlaps the tail and head of each loop.' },
  { id: 'pingpong', label: 'Ping-Pong', hint: 'Forward then reversed. Procedural scenes only.' },
];

export const VIDEO_STYLES = [
  { id: 'cinematic-realistic', label: 'Cinematic Realistic' },
  { id: 'documentary', label: 'Documentary' },
  { id: 'moody-noir', label: 'Moody Noir' },
  { id: 'clean-minimal', label: 'Clean Minimal' },
  { id: 'analog-film', label: 'Analog Film' },
];

export const DEFAULT_SETTINGS = {
  providerId: 'local-library',
  sceneMode: 'random',
  mood: 'TECH',
  resolution: '1080x1920',
  aspectRatio: '9:16',
  audioEffect: 'none',
  effectStrength: 35,
  loopMode: 'crossfade',
  crossfadeSeconds: 0.6,
  fps: 30,
  monitorAudio: false,
  audioReactive: true,
  audioReactiveStrength: 45,
  videoBitrateMbps: 8,
  audioBitrateKbps: 128,
  workerUrl: '',
  veoModel: 'veo-3.1-fast-generate-preview',
  veoResolution: '720p',
  veoDurationSeconds: '8',
  autoValidateImport: true,
  accentThumbnails: true,
};

/**
 * Verified platform facts. Each entry records where the claim came from
 * and when it was checked. Re-verify before shipping a new version.
 * See docs/RESEARCH.md for the full report.
 */
export const RESEARCH = {
  checkedOn: '2026-10-05',
  googleVids: {
    url: 'https://docs.google.com/videos/create',
    home: 'https://vids.new',
    providerType: 'manual',
    hasPublicGenerationApi: false,
    notes: [
      'Google Vids is a web application. It does not expose a documented public video-generation API.',
      'Official Google Workspace blog (2026-04-02): anyone with a Google account can generate video clips at no cost using Veo 3.1 inside Vids.',
      'Official Google Workspace blog (2026-04-02): personal accounts get 10 video generations per month at no cost.',
      'Official Google Workspace blog (2026-04-02): Google AI Ultra and Workspace AI Ultra accounts can generate up to 1,000 Veo videos per month.',
      'Limits and entitlements change. BEX LOOP never promises unlimited or permanently free access.',
    ],
    sources: [
      'https://blog.google/products-and-platforms/products/workspace/google-vids-updates-lyria-veo/',
    ],
  },
  googleVeo: {
    providerType: 'automated',
    api: 'Gemini API - POST {base}/models/{model}:predictLongRunning',
    base: 'https://generativelanguage.googleapis.com/v1beta',
    authHeader: 'x-goog-api-key',
    models: [
      'veo-3.1-generate-preview',
      'veo-3.1-fast-generate-preview',
      'veo-3.1-lite-generate-preview',
      'veo-3.0-generate-001',
      'veo-3.0-fast-generate-001',
    ],
    durations: ['4', '6', '8'],
    resolutions: ['720p', '1080p', '4k'],
    aspectRatios: ['16:9', '9:16'],
    fps: 24,
    nativeAudio: true,
    async: true,
    pollingField: 'operation.done',
    resultPath: 'response.generateVideoResponse.generatedSamples[0].video.uri',
    videoRetentionDays: 2,
    latency: { minSeconds: 11, maxSeconds: 360 },
    freeTier: false,
    pricePerSecond: {
      'veo-3.1-generate-preview': { '720p': 0.4, '1080p': 0.4, '4k': 0.6 },
      'veo-3.1-fast-generate-preview': { '720p': 0.1, '1080p': 0.12, '4k': 0.3 },
      'veo-3.1-lite-generate-preview': { '720p': 0.05, '1080p': 0.08 },
    },
    notes: [
      'Video generation is asynchronous: poll the returned operation until done is true.',
      'No free tier is published for any Veo model - every generation is billed per second.',
      '1080p and 4k require durationSeconds = "8".',
      'In EU, UK, CH and MENA locations personGeneration must be "allow_adult".',
      'Generated videos are retained server-side for 2 days; download within that window.',
    ],
    sources: [
      'https://ai.google.dev/gemini-api/docs/veo',
      'https://ai.google.dev/gemini-api/docs/pricing',
    ],
  },
  cloudflare: {
    workersFree: {
      requestsPerDay: 100000,
      cpuMsPerRequest: 10,
      memoryMb: 128,
      subrequestsPerRequest: 50,
      workerSizeMiB: 64,
      requestBodyMb: 100,
    },
    workersPaid: { cpuMsPerRequest: 300000, requestsPerDay: null },
    pagesFree: { buildsPerMonth: 500, concurrentBuilds: 1 },
    r2Free: { storageGbMonth: 10, classAOps: 1_000_000, classBOps: 10_000_000, egress: 'free' },
    notes: [
      'Workers are an API proxy and metadata layer, not a video processor: 128 MB memory and 10 ms CPU on the free plan.',
      'Cloudflare Pages serves the static frontend; Pages Functions are billed as Workers.',
      'R2 is optional. BEX LOOP is fully functional with browser-only storage.',
      'Cloudflare ToS discourages using the CDN as a general video streaming host - keep video in R2 or on the device.',
    ],
    sources: [
      'https://developers.cloudflare.com/workers/platform/limits/',
      'https://developers.cloudflare.com/workers/platform/pricing/',
    ],
  },
  browser: {
    mediaRecorder: {
      baseline: 'Widely available since April 2021',
      notes: [
        'Container support varies by browser; negotiate with MediaRecorder.isTypeSupported() at runtime.',
        'MP4 recording is used when the browser reports support, otherwise WebM. The real container is always reported to the user.',
      ],
    },
    webCodecs: {
      notes: [
        'WebCodecs gives per-frame encode control and is the recommended upgrade path for faster-than-realtime export.',
        'It only encodes/decodes; an external muxer is required to write a playable file.',
      ],
    },
  },
};

export const STORAGE = {
  dbName: 'bex-loop-studio',
  dbVersion: 1,
  stores: {
    assets: 'assets',
    scenes: 'scenes',
    prompts: 'prompts',
    history: 'history',
    settings: 'settings',
  },
  /** Warn before a single asset gets too large for comfortable mobile storage. */
  maxAssetBytes: 300 * 1024 * 1024,
  localStorageKey: 'bex-loop.settings.v1',
};

export const LIMITS = {
  /** Import validation thresholds (spec section 32). */
  minClipSeconds: 2,
  maxClipSeconds: 60,
  idealClipSeconds: [8, 15],
  aspectTolerance: 0.03,
  maxImportBytes: 300 * 1024 * 1024,
  /** Loop engine safety rail: refuse absurd repeat counts. */
  maxLoopRepeats: 2000,
  /** Veo duration choices surfaced in the cost-confirmation sheet. */
  veoDurations: ['4', '6', '8'],
};

export const MOODS = [
  { id: 'TECH', label: 'Tech', tags: ['technology', 'device', 'screen', 'computer'], color: '#FF7A00' },
  { id: 'DARK', label: 'Dark', tags: ['night', 'shadow', 'lowlight', 'moody'], color: '#5B6B8C' },
  { id: 'SERIOUS', label: 'Serious', tags: ['urban', 'documentary', 'restrained'], color: '#8C8C8C' },
  { id: 'CALM', label: 'Calm', tags: ['quiet', 'slow', 'ambient', 'soft'], color: '#4E8C7A' },
  { id: 'URBAN', label: 'Urban', tags: ['city', 'street', 'traffic', 'metro'], color: '#C0803A' },
  { id: 'FUTURISTIC', label: 'Futuristic', tags: ['abstract', 'digital', 'neon', 'data'], color: '#3A8CC0' },
  { id: 'NEWS', label: 'News', tags: ['office', 'desk', 'documentary', 'clean'], color: '#B03A3A' },
  { id: 'NIGHT', label: 'Night', tags: ['night', 'lights', 'rain', 'window'], color: '#3D4A6B' },
  { id: 'ENERGETIC', label: 'Energetic', tags: ['motion', 'fast', 'bright', 'city'], color: '#D9A400' },
  { id: 'NOSTALGIC', label: 'Nostalgic', tags: ['analog', 'tape', 'retro', 'warm'], color: '#A06B3A' },
];

export const AUDIO_EFFECTS = [
  { id: 'none', label: 'None', hint: 'Untouched audio.' },
  { id: 'headphones', label: 'Headphones', hint: 'Close, intimate, slightly narrowed stereo.' },
  { id: 'car_radio', label: 'Car Radio', hint: 'Mid-forward, band-limited, faint rumble.' },
  { id: 'telephone', label: 'Telephone', hint: 'Narrow band 300-3400 Hz, mono.' },
  { id: 'room', label: 'Room', hint: 'Short natural reverb, small room.' },
  { id: 'tape', label: 'Tape', hint: 'Soft saturation, gentle high roll-off, wow.' },
];
